import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  TablePagination,
} from "@material-ui/core";
import settingsIcon from "../../../assets/images/ct_settings.png";

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    height: "calc(100vh - 187px)",
    overflowX: "hidden",
  },
  table: {
    width: "calc(100vw - 297px)",
  },
  tableHead: {
    position: "sticky",
    top: 0,
    zIndex: 10,
  },
  headerCell: {
    backgroundColor: "#f5f5f5",
    fontFamily: "Roboto",
    fontWeight: 600,
    fontStyle: "normal",
    fontSize: 14,
    lineHeight: "100%",
    letterSpacing: "0%",
    textTransform: "uppercase",
    padding: theme.spacing(2),
    position: "sticky",
    top: 0,
    zIndex: 10,
    color: "#444444",
  },
  bodyCell: {
    padding: theme.spacing(2),
    fontSize: "14px",
    color: "#7A7A7A",
    fontFamily: "Roboto",
    fontWeight: 500,
    fontStyle: "normal",
    fontSize: 14,
    lineHeight: "110%",
    letterSpacing: "0%",
    maxWidth: "300px",
  },
  defaultText: {
    textTransform: "capitalize",
  },
  redText: {
    color: "#dc3545",
  },
  greenText: {
    color: "#28a745",
  },
  tableRow: {
    "&:hover": {
      backgroundColor: "#f9f9f9",
    },
  },
  actionCell: {
    paddingLeft: "35px !important",
  },
  settingsIcon: {
    cursor: "pointer",
    width: "24px",
    height: "24px",
  },
}));

const CustomTable = ({
  tableData,
  displayKeys,
  actions,
  rowsPerPageOptions = [10, 25, 50],
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  onChangePage,
  onChangeRowsPerPage,
  totalCount = 0,
}) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);

  // Get the keys from displayKeys object
  const columnKeys = Object.keys(displayKeys);

  const handleMenuOpen = (event, rowIndex) => {
    setAnchorEl(event.currentTarget);
    setSelectedRowIndex(rowIndex);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRowIndex(null);
  };

  const handleActionClick = (actionKey, rowData) => {
    if (actions && actions[actionKey]) {
      actions[actionKey](rowData);
    }
    handleMenuClose();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    onChangePage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const value = parseInt(event.target.value, 10);
    setRowsPerPage(value);
    setPage(0);
    onChangeRowsPerPage(value);
  };

  return (
    <Paper>
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table className={classes.table} aria-label="custom table" stickyHeader>
          <TableHead className={classes.tableHead}>
            <TableRow>
              {columnKeys.map((key) => (
                <TableCell key={key} className={classes.headerCell}>
                  {displayKeys[key]?.toString()?.length > 30
                    ? `${displayKeys[key]?.toString()?.slice(0, 30)}...`
                    : displayKeys[key]}
                </TableCell>
              ))}
              {actions && (
                <TableCell className={classes.headerCell}>ACTIONS</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {tableData?.map((row, index) => {
              // compute real index in original data (useful for menu logic)
              const realIndex = page * rowsPerPage + index;
              return (
                <TableRow key={realIndex} className={classes.tableRow}>
                  {columnKeys?.map((key) => {
                    const value =
                      row[key]?.toString()?.length > 50
                        ? `${row[key]?.toString()?.slice(0, 50)}...`
                        : row[key];

                    if (key === "paymentStatus") {
                      return (
                        <TableCell
                          key={key}
                          className={`${classes.bodyCell} ${
                            classes.defaultText
                          } ${
                            value === "closed"
                              ? classes.greenText
                              : value === "open" && classes.redText
                          }`}
                        >
                          {value}
                        </TableCell>
                      );
                    }

                    if (key === "priceString") {
                      return (
                        <TableCell key={key} className={classes.bodyCell}>
                          {row[key]}
                        </TableCell>
                      );
                    }

                    return (
                      <TableCell key={key} className={classes.bodyCell}>
                        {key === "credits" ? `${value} credits` : value}
                      </TableCell>
                    );
                  })}
                  {actions && (
                    <TableCell
                      className={`${classes.bodyCell} ${classes.actionCell}`}
                    >
                      <div
                        style={{ cursor: "pointer" }}
                        onClick={(event) => handleMenuOpen(event, realIndex)}
                      >
                        <img
                          src={settingsIcon}
                          alt="setting"
                          className={classes.settingsIcon}
                        />
                      </div>
                      <Menu
                        anchorEl={anchorEl}
                        keepMounted
                        open={
                          Boolean(anchorEl) && selectedRowIndex === realIndex
                        }
                        onClose={handleMenuClose}
                      >
                        {actions &&
                          Object.keys(actions)?.map((actionKey) => (
                            <MenuItem
                              key={actionKey}
                              onClick={() => handleActionClick(actionKey, row)}
                            >
                              {actionKey?.charAt(0)?.toUpperCase() +
                                actionKey?.slice(1)}
                            </MenuItem>
                          ))}
                      </Menu>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onChangePage={handleChangePage}
        rowsPerPage={rowsPerPage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        rowsPerPageOptions={rowsPerPageOptions}
      />
    </Paper>
  );
};

export default CustomTable;
